// §11.3 — Notepad: plain text and markdown, tabs, find/replace, word wrap, line numbers,
// autosave on a 2 s debounce, and the version history §7.5 gives every app for free.
//
// It holds no capability (D-059) — everything it does goes through `HangarAPI`, which means an
// untitled buffer has nowhere to autosave *to* until a shell-drawn picker grants it a path
// (§9.5). That is the design, not a gap: an editor that could write anywhere without a gesture
// would make the picker decorative.
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  basename,
  type HangarAPI,
  type NotepadShellAccess,
  type VersionEntry,
  type VfsPath,
} from '@hangar/shared';
import { COPY } from '@/copy';
import { NOTEPAD_AUTOSAVE_MS } from '@/config';
import {
  caretPosition,
  findMatches,
  lineCount,
  nextMatchIndex,
  replaceAll,
  replaceAt,
  untitledName,
} from './editor';

/**
 * §34.3 — the preview pane, on first use rather than at boot (M57.2).
 *
 * The `import()` is written here, in the file that renders it, so Vite splits at this point and the
 * renderer, this view and the preview's own styles leave Notepad's eager bundle. Notepad is in
 * `BUILTIN_APPS`, so anything it imports statically is downloaded before first paint by everyone,
 * whether or not they ever open a `.md`.
 */
const Preview = lazy(() => import('./Preview'));

interface Tab {
  id: string;
  /** `null` until a picker grants one (§9.5). */
  path: VfsPath | null;
  name: string;
  text: string;
  /** What is on disk. `text !== saved` is the whole definition of dirty. */
  saved: string;
  savedAt: number | null;
  /** §34.3 — the preview pane, **per tab**: a `.md` and a `.log` open differently. */
  preview: boolean;
}

let nextTabId = 0;

function newTab(name: string, path: VfsPath | null = null, text = '', preview = false): Tab {
  nextTabId += 1;
  // A brand-new empty buffer is not dirty; the first keystroke is what makes it so.
  return { id: `t${nextTabId}`, path, name, text, saved: text, savedAt: null, preview };
}

/** §34.3 — the extensions the preview is offered for. `notepad.mdDefaultView` picks the pane. */
export function isMarkdown(name: string): boolean {
  return /\.(md|markdown)$/i.test(name);
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isDirty(tab: Tab): boolean {
  return tab.text !== tab.saved;
}

/**
 * §5.6 — read back what this window saved, defensively. The shell hands the blob straight back
 * without looking inside it, so anything at all can arrive here: a blob from an older Notepad,
 * a corrupted one, or `null`. Anything unrecognised means "open empty" (§18 F12).
 */
function readSessionBlob(blob: unknown): { paths: VfsPath[]; active: VfsPath | null } {
  const empty = { paths: [], active: null };
  if (typeof blob !== 'object' || blob === null) return empty;
  const shaped = blob as { v?: unknown; paths?: unknown; active?: unknown };
  if (shaped.v !== 1 || !Array.isArray(shaped.paths)) return empty;
  return {
    paths: shaped.paths.filter((path): path is VfsPath => typeof path === 'string'),
    active: typeof shaped.active === 'string' ? shaped.active : null,
  };
}

export function Notepad({ api, access }: { api: HangarAPI; access: NotepadShellAccess }) {
  const fs = api.fs;
  const [tabs, setTabs] = useState<Tab[]>(() => [newTab(COPY.notepad.untitled)]);
  const [activeId, setActiveId] = useState<string>(() => tabs[0]?.id ?? '');
  const [wrap, setWrap] = useState(true);
  const [finding, setFinding] = useState(false);
  const [query, setQuery] = useState('');
  const [replacement, setReplacement] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [findNote, setFindNote] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [caret, setCaret] = useState(0);
  const [status, setStatus] = useState<string | null>(null);

  const area = useRef<HTMLTextAreaElement | null>(null);
  const gutter = useRef<HTMLDivElement | null>(null);
  const findInput = useRef<HTMLInputElement | null>(null);

  const active = useMemo(
    () => tabs.find((tab) => tab.id === activeId) ?? tabs[0] ?? null,
    [tabs, activeId],
  );

  const patch = useCallback((id: string, change: Partial<Tab>) => {
    setTabs((current) => current.map((tab) => (tab.id === id ? { ...tab, ...change } : tab)));
  }, []);

  // ── the markdown preview (§34.3) ──────────────────────────────────────────────────────────

  /**
   * A tab opened from a file, with the pane §14 says a `.md` opens on.
   *
   * Read at open time rather than watched: the setting is a *default*, and a document already open
   * flipping panes underneath a reader because they changed a preference is not what a default means.
   */
  const openTab = useCallback(
    (name: string, path: VfsPath | null, text: string): Tab =>
      newTab(name, path, text, isMarkdown(name) && access.mdDefaultView() === 'preview'),
    [access],
  );

  /**
   * What the preview is rendering — the text as of the last **2 s autosave debounce** (§34.3), not
   * the live buffer.
   *
   * Re-parsing and re-rendering on every keystroke would be work thrown away by the next one, and
   * an image-bearing document would re-read its images each time. The first render after the pane
   * opens is immediate, because a pane that appears blank for two seconds looks broken.
   */
  const [previewText, setPreviewText] = useState('');
  const primedFor = useRef<string>('');
  useEffect(() => {
    if (!active || !active.preview) return;
    if (primedFor.current !== active.id) {
      primedFor.current = active.id;
      setPreviewText(active.text);
      return;
    }
    const timer = setTimeout(() => setPreviewText(active.text), NOTEPAD_AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [active]);

  const togglePreview = useCallback(() => {
    if (!active) return;
    // Re-prime, so re-opening a pane shows the document as it is now rather than as it was.
    primedFor.current = '';
    patch(active.id, { preview: !active.preview });
  }, [active, patch]);

  // ── saving ───────────────────────────────────────────────────────────────────────────────

  const writeTab = useCallback(
    async (tab: Tab, path: VfsPath): Promise<void> => {
      const text = tab.text;
      const entry = await fs.write(path, text);
      patch(tab.id, { path, name: basename(entry.path), saved: text, savedAt: Date.now() });
    },
    [fs, patch],
  );

  /** Save As: the picker is the only way an editor gets a path outside its own space (§9.5). */
  const saveAs = useCallback(
    async (tab: Tab): Promise<void> => {
      const entry = await fs.pickSave({
        title: COPY.notepad.saveAs,
        suggestedName: tab.path ? basename(tab.path) : `${tab.name}.txt`,
      });
      await writeTab(tab, entry.path);
    },
    [fs, writeTab],
  );

  const save = useCallback(
    (tab: Tab): void => {
      const work = tab.path ? writeTab(tab, tab.path) : saveAs(tab);
      void work.catch((cause: unknown) => {
        // A dismissed picker is `CANCELLED` (D-072) — the user changing their mind, not an error.
        const code = (cause as { code?: unknown }).code;
        if (code !== 'CANCELLED') setStatus(messageOf(cause));
      });
    },
    [saveAs, writeTab],
  );

  // §11.3 — autosave, 2 s after the last keystroke, for every tab that has somewhere to go.
  useEffect(() => {
    const pending = tabs.filter((tab) => tab.path !== null && isDirty(tab));
    if (pending.length === 0) return;
    const timer = setTimeout(() => {
      for (const tab of pending) {
        const path = tab.path;
        if (path) void writeTab(tab, path).catch((cause: unknown) => setStatus(messageOf(cause)));
      }
    }, NOTEPAD_AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [tabs, writeTab]);

  /**
   * §5.6 — what this window hands the shell to get back on the next boot: the files it had open
   * and which one was in front. Only *saved* tabs, because a path is all the shell keeps and an
   * untitled buffer has none (D-079).
   *
   * Gated on the read below having happened. On mount this window holds one empty tab, and
   * writing *that* over the blob would erase the session a moment before reading it.
   */
  const sessionRead = useRef(false);
  useEffect(() => {
    if (!sessionRead.current) return;
    const paths = tabs
      .map((tab) => tab.path)
      .filter((path): path is VfsPath => path !== null);
    void api.session.save({ v: 1, paths, active: active?.path ?? null });
  }, [api, tabs, active]);

  // …and the other half: whatever it saved last time, read back once on mount. A blob it cannot
  // make sense of means it opens empty — restore never blocks the desktop (§5.6, §18 F12).
  useEffect(() => {
    let cancelled = false;
    void api.session
      .load()
      .then(async (blob) => {
        const saved = readSessionBlob(blob);
        if (cancelled || saved.paths.length === 0) return;
        const opened: Tab[] = [];
        for (const path of saved.paths) {
          try {
            opened.push(openTab(basename(path), path, await fs.readText(path)));
          } catch {
            // A file that has since been deleted or moved is simply not reopened.
          }
        }
        if (cancelled || opened.length === 0) return;
        setTabs((current) => [...current.filter((tab) => isDirty(tab)), ...opened]);
        const front = opened.find((tab) => tab.path === saved.active) ?? opened[opened.length - 1];
        if (front) setActiveId(front.id);
      })
      .catch(() => undefined)
      .finally(() => {
        sessionRead.current = true;
      });
    return () => {
      cancelled = true;
    };
    // Mount only — `api` and `fs` are stable for the life of the window, and reopening on any
    // later render would fight the user closing a tab.
  }, [api, fs]);

  // §9.2 — this window may have been opened *for* a file, by Explorer's Open with. The path is
  // already granted (§9.5) by the time the app mounts, so the read simply works.
  useEffect(() => {
    let cancelled = false;
    void api.launch
      .openedPath()
      .then(async (path) => {
        if (cancelled || path === null) return;
        const text = await fs.readText(path);
        if (cancelled) return;
        const tab = openTab(basename(path), path, text);
        // The blank tab a fresh window starts with is noise once we know what to open.
        setTabs((current) => [...current.filter((existing) => isDirty(existing)), tab]);
        setActiveId(tab.id);
      })
      .catch((cause: unknown) => setStatus(messageOf(cause)));
    return () => {
      cancelled = true;
    };
  }, [api, fs]);

  // The window chrome is the honest place for "this has unsaved changes" (§5.1).
  useEffect(() => {
    if (!active) return;
    void api.window.setTitle(active.name);
    void api.window.setDirty(isDirty(active));
  }, [api, active]);

  // ── opening ──────────────────────────────────────────────────────────────────────────────

  const openFiles = useCallback(() => {
    void fs
      .pickOpen({ title: COPY.notepad.open, multiple: true, extensions: NOTEPAD_EXTENSIONS })
      .then(async (picked) => {
        for (const entry of picked) {
          const text = await fs.readText(entry.path);
          const tab = openTab(entry.name, entry.path, text);
          setTabs((current) => [...current, tab]);
          setActiveId(tab.id);
        }
      })
      .catch((cause: unknown) => setStatus(messageOf(cause)));
  }, [fs]);

  const closeTab = useCallback(
    (tab: Tab) => {
      const drop = (): void => {
        setTabs((current) => {
          const remaining = current.filter((candidate) => candidate.id !== tab.id);
          const next = remaining.length > 0 ? remaining : [newTab(COPY.notepad.untitled)];
          setActiveId((currentId) =>
            currentId === tab.id ? (next[next.length - 1]?.id ?? '') : currentId,
          );
          return next;
        });
      };
      if (!isDirty(tab)) {
        drop();
        return;
      }
      // §19.4 — the shell draws the confirm; an app cannot fake this one.
      void api.dialog.confirm(COPY.notepad.discard(tab.name)).then((yes) => {
        if (yes) drop();
      });
    },
    [api],
  );

  // ── version history (§7.5) ───────────────────────────────────────────────────────────────

  const loadVersions = useCallback(
    (path: VfsPath | null) => {
      if (!path) {
        setVersions([]);
        return;
      }
      void fs
        .versions(path)
        .then(setVersions)
        .catch(() => setVersions([]));
    },
    [fs],
  );

  useEffect(() => {
    if (!historyOpen) return;
    loadVersions(active?.path ?? null);
  }, [historyOpen, active?.path, active?.savedAt, loadVersions]);

  const restore = useCallback(
    (version: VersionEntry) => {
      const tab = active;
      const path = tab?.path;
      if (!tab || !path) return;
      void fs
        .restore(path, version.id)
        .then(() => fs.readText(path))
        .then((text) => {
          // §7.5 — a restore is itself a write, so it appends a version and is undoable.
          patch(tab.id, { text, saved: text, savedAt: Date.now() });
          loadVersions(path);
        })
        .catch((cause: unknown) => setStatus(messageOf(cause)));
    },
    [active, fs, loadVersions, patch],
  );

  // ── find and replace ─────────────────────────────────────────────────────────────────────

  const matches = useMemo(
    () => findMatches(active?.text ?? '', query, matchCase),
    [active?.text, query, matchCase],
  );

  const jump = useCallback(
    (forward: boolean) => {
      const node = area.current;
      if (!node || !active) return;
      if (matches.length === 0) {
        setFindNote(query.length === 0 ? '' : COPY.notepad.noMatches);
        return;
      }
      const from = forward ? node.selectionEnd : node.selectionStart;
      const index = nextMatchIndex(matches, from, forward);
      const match = matches[index];
      if (!match) return;
      node.focus();
      node.setSelectionRange(match.start, match.end);
      setFindNote(COPY.notepad.matchCount(index + 1, matches.length));
    },
    [active, matches, query],
  );

  const replaceCurrent = useCallback(() => {
    const node = area.current;
    if (!node || !active || matches.length === 0) return;
    const selected = matches.find(
      (match) => match.start === node.selectionStart && match.end === node.selectionEnd,
    );
    const match = selected ?? matches[nextMatchIndex(matches, node.selectionStart, true)];
    if (!match) return;
    patch(active.id, { text: replaceAt(active.text, match, replacement) });
    setFindNote(COPY.notepad.replacedCount(1));
  }, [active, matches, patch, replacement]);

  const replaceEvery = useCallback(() => {
    if (!active) return;
    const result = replaceAll(active.text, query, replacement, matchCase);
    patch(active.id, { text: result.text });
    setFindNote(COPY.notepad.replacedCount(result.count));
  }, [active, matchCase, patch, query, replacement]);

  useEffect(() => {
    if (finding) findInput.current?.focus();
  }, [finding]);

  // ── keyboard (§9.7 — the shell's reserved chords never reach here) ───────────────────────

  const addTab = useCallback(() => {
    setTabs((current) => {
      const tab = newTab(
        untitledName(
          current.map((existing) => existing.name),
          COPY.notepad.untitled,
        ),
      );
      setActiveId(tab.id);
      return [...current, tab];
    });
  }, []);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      const accel = event.ctrlKey || event.metaKey;
      if (!accel) return;
      const key = event.key.toLowerCase();
      if (key === 's') {
        event.preventDefault();
        if (active) save(active);
      } else if (key === 'o') {
        event.preventDefault();
        openFiles();
      } else if (key === 'n') {
        event.preventDefault();
        addTab();
      } else if (key === 'f') {
        event.preventDefault();
        setFinding(true);
      } else if (key === 'p' && event.shiftKey) {
        // §34.3 — `⌘/Ctrl+Shift+P`. Not a §6.4 reserved chord: it is Notepad's own, so it only
        // fires while a Notepad window has focus, which is what an app-level binding means (§9.7).
        event.preventDefault();
        togglePreview();
      }
    },
    [active, addTab, openFiles, save, togglePreview],
  );

  const lines = lineCount(active?.text ?? '');
  const position = caretPosition(active?.text ?? '', caret);

  return (
    <div className="notepad" data-testid="notepad" onKeyDown={onKeyDown}>
      <div className="notepad-tabs" role="tablist" data-testid="notepad-tabs">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className="notepad-tab"
            data-active={String(tab.id === activeId)}
            data-dirty={String(isDirty(tab))}
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab.id === activeId}
              className="notepad-tab-label"
              data-testid={`notepad-tab-${tab.name}`}
              onClick={() => setActiveId(tab.id)}
            >
              {tab.name}
              {isDirty(tab) ? ' •' : ''}
            </button>
            <button
              type="button"
              className="notepad-tab-close"
              aria-label={COPY.notepad.closeTab(tab.name)}
              data-testid={`notepad-close-${tab.name}`}
              onClick={() => closeTab(tab)}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-new"
          onClick={() => addTab()}
        >
          {COPY.notepad.newTab}
        </button>
      </div>

      <div className="notepad-toolbar">
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-open"
          onClick={openFiles}
        >
          {COPY.notepad.open}
        </button>
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-save"
          onClick={() => active && save(active)}
        >
          {COPY.notepad.save}
        </button>
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-save-as"
          onClick={() => active && void saveAs(active).catch(() => undefined)}
        >
          {COPY.notepad.saveAs}
        </button>
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-find"
          data-active={String(finding)}
          onClick={() => setFinding((value) => !value)}
        >
          {COPY.notepad.find}
        </button>
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-wrap"
          data-active={String(wrap)}
          onClick={() => setWrap((value) => !value)}
        >
          {COPY.notepad.wordWrap}
        </button>
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-preview-toggle"
          data-active={String(active?.preview ?? false)}
          aria-pressed={active?.preview ?? false}
          onClick={togglePreview}
        >
          {COPY.notepad.preview}
        </button>
        <button
          type="button"
          className="notepad-button"
          data-testid="notepad-history"
          data-active={String(historyOpen)}
          onClick={() => setHistoryOpen((value) => !value)}
        >
          {COPY.notepad.history}
        </button>
      </div>

      {finding ? (
        <div className="notepad-find" data-testid="notepad-find-bar">
          <input
            ref={findInput}
            data-testid="notepad-find-input"
            placeholder={COPY.notepad.findPlaceholder}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setFindNote('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                jump(!event.shiftKey);
              }
              if (event.key === 'Escape') setFinding(false);
            }}
          />
          <input
            data-testid="notepad-replace-input"
            placeholder={COPY.notepad.replacePlaceholder}
            value={replacement}
            onChange={(event) => setReplacement(event.target.value)}
          />
          <button
            type="button"
            className="notepad-button"
            data-testid="notepad-find-prev"
            onClick={() => jump(false)}
          >
            {COPY.notepad.findPrevious}
          </button>
          <button
            type="button"
            className="notepad-button"
            data-testid="notepad-find-next"
            onClick={() => jump(true)}
          >
            {COPY.notepad.findNext}
          </button>
          <button
            type="button"
            className="notepad-button"
            data-testid="notepad-replace"
            onClick={replaceCurrent}
          >
            {COPY.notepad.replace}
          </button>
          <button
            type="button"
            className="notepad-button"
            data-testid="notepad-replace-all"
            onClick={replaceEvery}
          >
            {COPY.notepad.replaceAll}
          </button>
          <label className="notepad-case">
            <input
              type="checkbox"
              data-testid="notepad-match-case"
              checked={matchCase}
              onChange={(event) => setMatchCase(event.target.checked)}
            />
            {COPY.notepad.matchCase}
          </label>
          <span className="notepad-find-note" data-testid="notepad-find-note">
            {findNote}
          </span>
        </div>
      ) : null}

      <div className="notepad-body" data-preview={String(active?.preview ?? false)}>
        <div className="notepad-editor">
          <div className="notepad-gutter" ref={gutter} aria-hidden="true" data-testid="notepad-gutter">
            {Array.from({ length: lines }, (_unused, index) => (
              <div key={index}>{index + 1}</div>
            ))}
          </div>
          <textarea
            ref={area}
            className="notepad-text"
            data-testid="notepad-text"
            data-wrap={String(wrap)}
            spellCheck={false}
            wrap={wrap ? 'soft' : 'off'}
            value={active?.text ?? ''}
            onChange={(event) => {
              if (active) patch(active.id, { text: event.target.value });
              setCaret(event.target.selectionStart);
            }}
            onSelect={(event) => setCaret((event.target as HTMLTextAreaElement).selectionStart)}
            onScroll={(event) => {
              const node = gutter.current;
              if (node) node.scrollTop = (event.target as HTMLTextAreaElement).scrollTop;
            }}
          />
        </div>

        {/* §34.3 — 50/50 with the editor, and only while this tab asked for it. The pane is a
            sibling of the editor rather than a wrapper around it, so toggling it never remounts the
            textarea and never loses the caret. */}
        {active?.preview ? (
          <Suspense fallback={<div className="notepad-preview" data-testid="notepad-preview-loading" />}>
            <Preview text={previewText} path={active.path} api={api} access={access} />
          </Suspense>
        ) : null}

        {historyOpen ? (
          <aside className="notepad-history" aria-label={COPY.notepad.history} data-testid="notepad-history-panel">
            {versions.map((version) => (
              <div key={version.id} className="notepad-version" data-testid={`notepad-version-${version.id}`}>
                <span>{new Date(version.createdAt).toLocaleTimeString()}</span>
                <span className="notepad-version-size">{version.size} B</span>
                <button
                  type="button"
                  className="notepad-button"
                  data-testid={`notepad-restore-${version.id}`}
                  onClick={() => restore(version)}
                >
                  {COPY.notepad.restore}
                </button>
              </div>
            ))}
            {versions.length === 0 ? (
              <p className="notepad-empty" data-testid="notepad-history-empty">
                {COPY.notepad.historyEmpty}
              </p>
            ) : null}
          </aside>
        ) : null}
      </div>

      <footer className="notepad-status" data-testid="notepad-status">
        {status ??
          (active?.path
            ? isDirty(active)
              ? COPY.notepad.saving
              : COPY.notepad.savedAt(
                  active.savedAt === null
                    ? active.path
                    : new Date(active.savedAt).toLocaleTimeString(),
                )
            : COPY.notepad.unsaved)}
        <span className="notepad-position" data-testid="notepad-position">
          {COPY.notepad.position(position.line, position.column)}
        </span>
      </footer>
    </div>
  );
}

/** §11.3's registered types, and what the Open picker filters to. */
export const NOTEPAD_EXTENSIONS = ['.txt', '.md', '.markdown', '.log', '.json', '.csv'];
